import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import './utils/redirectHandler'; // Import redirect handler
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import NewPassword from './components/auth/NewPassword';
import PasswordSuccess from './components/auth/PasswordSuccess';
import SetPassword from './components/auth/SetPassword';
import AddUser from './components/auth/AddUser';
import LeadsAndEstimates from './components/leads/LeadsAndEstimates';
import Projects from './components/projects/Projects';
import TimeManagement from './components/hr/TimeManagement';
import Safety from './components/safety/Safety';
import DashboardHome from './components/dashboard/DashboardHome';
import NewLead from './components/leads/NewLead';
import PrivateRoute from './components/utils/PrivateRoute';
import './styles/dashboard.css';
import { config } from './config/config';
import { initializeAuth, logout, hideSessionExpiredDialog, showSessionExpiredDialog } from './store/slices/authSlice';
import { fetchAppSettings } from './store/slices/settingsSlice';
import MapView from './components/leads/MapView';
import ProposalView from './components/proposals/ProposalView';
import ProjectMapView from './components/projects/ProjectMapView';
import ProjectCalendar from './components/projects/ProjectCalendar';
import CalendarPage from './components/calendar/CalendarPage';
import './styles/calendar.css';
import WeatherAndMap from './components/weather/WeatherAndMap';
import RoofMeasurement from './components/roofmeasure/RoofMeasurement';
import ChangeOrderView from './components/projects/ChangeOrderView';
import Clients from './components/clients/Clients';
import WebsiteInquiries from './components/inquiries/WebsiteInquiries';
import LoadingOverlay from './components/common/LoadingOverlay';
import SessionExpiredDialog from './components/common/SessionExpiredDialog';
import TokenCheckWrapper from './components/utils/TokenCheckWrapper';
import QuickbooksCallback from './components/quickbooks/QuickbooksCallback';
import AIChat from './components/ai/AIChat';

const API_URL = config.apiUrl;

function App() {
  const { isAuthenticated, token, showSessionExpiredDialog: isSessionExpiredDialogOpen } = useSelector((state) => state.auth);
  const { activeMenuItem } = useSelector((state) => state.menu);
  const dispatch = useDispatch();

  useEffect(() => {
    // Initialize auth state from localStorage
    dispatch(initializeAuth());

    // Token expiration is now handled by TokenCheckWrapper
    // This avoids duplicate token checks in multiple places
  }, [dispatch]);

  // Once authenticated, load global app settings (pricing defaults, JWT settings, etc.)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchAppSettings());
    }
  }, [dispatch, isAuthenticated]);

  // Determine the default redirect path based on stored menu selection
  const getDefaultPath = () => {
    if (activeMenuItem && activeMenuItem !== '/') {
      return activeMenuItem;
    }
    return '/leads';
  };

  return (
    <>
      <LoadingOverlay />
      <Router>
        <TokenCheckWrapper>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to={getDefaultPath()} replace /> : <Login />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<NewPassword />} />
            <Route path="/password-success" element={<PasswordSuccess />} />
            <Route path="/set-password" element={<SetPassword />} />

            {/* Protected Routes */}
            <Route path="/" element={
              <PrivateRoute>
                <DashboardHome />
              </PrivateRoute>
            } />
            <Route path="/leads" element={
              <PrivateRoute>
                <LeadsAndEstimates />
              </PrivateRoute>
            } />
            <Route path="/projects" element={
              <PrivateRoute>
                <Projects />
              </PrivateRoute>
            } />
            <Route path="/hr" element={
              <PrivateRoute>
                <TimeManagement />
              </PrivateRoute>
            } />
            <Route path="/safety" element={
              <PrivateRoute>
                <Safety />
              </PrivateRoute>
            } />
            <Route path="/clients" element={
              <PrivateRoute>
                <Clients />
              </PrivateRoute>
            } />
            <Route path="/inquiries" element={
              <PrivateRoute>
                <WebsiteInquiries />
              </PrivateRoute>
            } />
            <Route path="/users/add" element={
              <AddUser />
            } />
            <Route path="/leads/new" element={
              <PrivateRoute>
                <NewLead />
              </PrivateRoute>
            } />
            <Route path="/leads/map" element={
              <PrivateRoute>
                <MapView />
              </PrivateRoute>
            } />
            <Route path="/proposals/:id/view" element={<ProposalView />} />
            <Route path="/projects/map" element={<ProjectMapView />} />
            <Route path="/projects/calendar" element={
              <PrivateRoute>
                <CalendarPage />
              </PrivateRoute>
            } />
            <Route path="/calendar" element={
              <PrivateRoute>
                <CalendarPage />
              </PrivateRoute>
            } />
            <Route path="/calendar/legacy" element={
              <PrivateRoute>
                <ProjectCalendar />
              </PrivateRoute>
            } />
            <Route path="/weather" element={
              <PrivateRoute>
                <WeatherAndMap />
              </PrivateRoute>
            } />
            <Route path="/roof-measurement" element={
              <PrivateRoute>
                <RoofMeasurement />
              </PrivateRoute>
            } />
            <Route path="/change-orders/:id/view" element={<ChangeOrderView />} />
            <Route path="/quickbooks-callback" element={<QuickbooksCallback />} />
            <Route path="/ai" element={
              <PrivateRoute>
                <AIChat />
              </PrivateRoute>
            } />
          </Routes>
          <SessionExpiredDialog
            isOpen={isSessionExpiredDialogOpen}
            onClose={() => dispatch(hideSessionExpiredDialog())}
          />
        </TokenCheckWrapper>
      </Router>
      <div id="toast-container" />
    </>
  );
}

export default App;
